import { useState, useCallback } from "react";
import { useTeam } from "@/hooks/useTeam";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Users, Minus, Plus, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

// Tiered pricing (SAR) — must match edge functions
const pricingTiers = [
  { min: 1, max: 9, monthlyPrice: 55, yearlyPrice: 35 },
  { min: 10, max: 49, monthlyPrice: 44, yearlyPrice: 27 },
  { min: 50, max: Infinity, monthlyPrice: 33, yearlyPrice: 19 },
];

function calculateTeamTotal(seats: number, billingCycle: "monthly" | "yearly"): number {
  let remaining = seats;
  let seatIndex = 1;
  let total = 0;

  for (const tier of pricingTiers) {
    if (remaining <= 0) break;
    const tierStart = Math.max(seatIndex, tier.min);
    const tierEnd = Math.min(seatIndex + remaining - 1, tier.max);
    const seatsInTier = tierEnd - tierStart + 1;

    if (seatsInTier > 0 && seatIndex <= tier.max) {
      const pricePerSeat = billingCycle === "monthly" ? tier.monthlyPrice : tier.yearlyPrice;
      total += seatsInTier * pricePerSeat;
      remaining -= seatsInTier;
      seatIndex = tierEnd + 1;
    }
  }

  if (billingCycle === "yearly") {
    total *= 12;
  }

  return total;
}

function getPerSeatPrice(seats: number, billingCycle: "monthly" | "yearly"): number {
  for (const tier of pricingTiers) {
    if (seats >= tier.min && seats <= tier.max) {
      return billingCycle === "monthly" ? tier.monthlyPrice : tier.yearlyPrice;
    }
  }
  return pricingTiers[0].monthlyPrice;
}

export function TeamBillingPanel() {
  const { team, members, loading, refetch } = useTeam();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [seatsDialogOpen, setSeatsDialogOpen] = useState(false);
  const [pendingSeats, setPendingSeats] = useState(3);
  const [saving, setSaving] = useState(false);

  const openSeatsDialog = useCallback(() => {
    if (team) {
      setPendingSeats(team.seat_count);
      setSeatsDialogOpen(true);
    }
  }, [team]);

  const handleSaveSeats = async () => {
    if (!team || pendingSeats === team.seat_count) {
      setSeatsDialogOpen(false);
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.functions.invoke("team-seat-sync", {
        body: { team_id: team.id, new_seat_count: pendingSeats },
      });
      if (error) throw error;
      toast({ title: "Seats updated", description: `Team now has ${pendingSeats} seats.` });
      await refetch();
      setSeatsDialogOpen(false);
    } catch (err: unknown) {
      toast({
        title: "Failed to update seats",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <span className="text-sm text-muted-foreground">Loading team billing data...</span>
      </div>
    );
  }

  if (!team) {
    return null;
  }

  const seatUsagePercent = team.seat_count > 0 ? (members.length / team.seat_count) * 100 : 0;
  const isActive = team.subscription_status === "active";
  const billingCycle = (team.billing_cycle as "monthly" | "yearly") || "monthly";
  const billingLabel = billingCycle === "yearly" ? "/year" : "/month";

  const currentTotal = calculateTeamTotal(team.seat_count, billingCycle);
  const pendingTotal = calculateTeamTotal(pendingSeats, billingCycle);
  const pendingPerSeat = getPerSeatPrice(pendingSeats, billingCycle);
  const minSeats = Math.max(3, members.length);

  const getPriceDisplay = () => {
    return `SAR ${currentTotal}`;
  };

  return (
    <>
      <ScrollArea className="h-[calc(85vh-10rem)] md:h-[calc(600px-8rem)] pr-2 md:pr-4">
        <div className="space-y-0">
          {/* Plan Header */}
          <div className="flex items-center justify-between py-3 border-b">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-medium">Team Plan</h3>
                <Badge variant={isActive ? "default" : "secondary"} className={isActive ? "bg-primary" : ""}>
                  {isActive ? "Active" : (team.subscription_status || "Inactive")}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Shared access for your entire team
              </p>
            </div>
            <div className="text-right">
              <p className="text-lg font-semibold">
                {getPriceDisplay()}
                <span className="text-sm text-muted-foreground font-normal"> {billingLabel}</span>
              </p>
            </div>
          </div>

          {/* Seats Usage */}
          <div className="pt-4 pb-2">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Seat Usage</h4>
          </div>
          <div className="py-3 border-b space-y-2">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Seats</span>
              </div>
              <span className="text-muted-foreground">
                {members.length} / {team.seat_count} used
              </span>
            </div>
            <Progress value={seatUsagePercent} className="h-2" />
            <p className="text-xs text-muted-foreground">
              {team.seat_count - members.length} seat{team.seat_count - members.length !== 1 ? "s" : ""} available
            </p>
          </div>

          {/* Subscription Details */}
          <div className="pt-4 pb-2">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Subscription Details</h4>
          </div>
          <div className="space-y-0">
            <div className="flex items-center justify-between py-3 border-b">
              <span className="text-sm font-medium">Billing Cycle</span>
              <span className="text-sm text-muted-foreground capitalize">{billingCycle}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b">
              <span className="text-sm font-medium">Next Billing Date</span>
              <span className="text-sm text-muted-foreground">
                {team.subscription_period_end
                  ? new Date(team.subscription_period_end).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })
                  : "—"}
              </span>
            </div>
          </div>

          {/* Usage & Limits */}
          <div className="pt-4 pb-2">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Usage & Limits</h4>
          </div>
          <div className="space-y-0">
            {["Uploads", "AI Chats", "Exams", "Flashcards", "Quizzes"].map((item) => (
              <div key={item} className="flex items-center justify-between py-3 border-b">
                <span className="text-sm font-medium">{item}</span>
                <span className="text-xs text-muted-foreground font-extrabold">Unlimited</span>
              </div>
            ))}
          </div>

          {/* Manage Plan */}
          <div className="pt-4 pb-2">
            <h3 className="text-lg font-semibold">Manage Plan</h3>
          </div>
          <div className="py-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Button variant="outline" onClick={openSeatsDialog}>
                <Users className="h-4 w-4 mr-1" />
                Manage Seats
              </Button>
              <Button
                variant="default"
                onClick={() => navigate("/pricing")}
              >
                {billingCycle === "yearly" ? "Switch to Monthly" : "Switch to Yearly"}
              </Button>
              <Button variant="default">
                Update Payment
              </Button>
            </div>
          </div>
        </div>
      </ScrollArea>

      {/* Manage Seats Dialog */}
      <Dialog open={seatsDialogOpen} onOpenChange={setSeatsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Seats</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Seat counter */}
            <div className="flex items-center justify-center gap-4">
              <Button
                variant="outline"
                size="icon"
                disabled={pendingSeats <= minSeats || saving}
                onClick={() => setPendingSeats((s) => Math.max(minSeats, s - 1))}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <div className="text-center min-w-[80px]">
                <p className="text-3xl font-bold">{pendingSeats}</p>
                <p className="text-xs text-muted-foreground">seats</p>
              </div>
              <Button
                variant="outline"
                size="icon"
                disabled={saving}
                onClick={() => setPendingSeats((s) => s + 1)}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {/* Pricing breakdown */}
            <div className="rounded-lg border p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Per seat</span>
                <span className="font-medium">SAR {pendingPerSeat}{billingCycle === "yearly" ? "/mo" : "/mo"}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Seats</span>
                <span className="font-medium">× {pendingSeats}</span>
              </div>
              <div className="border-t pt-2 flex justify-between text-sm font-semibold">
                <span>Total</span>
                <span>SAR {pendingTotal}{billingLabel}</span>
              </div>
              {pendingSeats !== team.seat_count && (
                <p className="text-xs text-muted-foreground pt-1">
                  {pendingSeats > team.seat_count
                    ? `Adding ${pendingSeats - team.seat_count} seat${pendingSeats - team.seat_count !== 1 ? "s" : ""}`
                    : `Removing ${team.seat_count - pendingSeats} seat${team.seat_count - pendingSeats !== 1 ? "s" : ""}`}
                  {" · "}
                  {pendingTotal > currentTotal
                    ? `+SAR ${pendingTotal - currentTotal}`
                    : `-SAR ${currentTotal - pendingTotal}`}{billingLabel}
                </p>
              )}
            </div>

            {pendingSeats <= minSeats && members.length >= minSeats && (
              <p className="text-xs text-destructive text-center">
                Cannot go below {minSeats} seats ({members.length} members active)
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSeatsDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveSeats}
              disabled={saving || pendingSeats === team.seat_count}
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {pendingSeats === team.seat_count ? "No changes" : "Update Seats"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
